import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Loader2, KeyRound, ShieldCheck } from "lucide-react";

export default function SetupPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);     // {email, name}
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/auth/setup-password/${token}`)
      .then(({ data }) => { if (!cancelled) { setInfo(data); setLoading(false); } })
      .catch((err) => {
        if (!cancelled) {
          setError(formatApiErrorDetail(err.response?.data?.detail) || "Link non valido");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("La password deve contenere almeno 8 caratteri"); return; }
    if (pwd !== pwd2) { toast.error("Le due password non coincidono"); return; }
    if (!consent) { toast.error("Devi accettare il regolamento per attivare l'account"); return; }
    setSubmitting(true);
    try {
      const { data: user } = await api.post(`/auth/setup-password/${token}`, {
        new_password: pwd,
        consent_regolamento: true,
      });
      await refresh();
      toast.success(`Benvenuto ${user.name?.split(" ")[0] || ""}! Inizia a presentare amici.`);
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore attivazione account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9]" data-testid="setup-password-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F7B85]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9] p-6" data-testid="setup-password-error">
        <div className="w-full max-w-md text-center bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-rose-600" />
          </div>
          <h1 className="font-display text-2xl mt-4 text-stone-900">Link non valido</h1>
          <p className="mt-3 text-slate-600 text-sm">{error}</p>
          <Link to="/login" className="inline-block mt-6 bg-[#0F7B85] text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-[#0c6973]">
            Vai al login
          </Link>
          <p className="mt-4 text-xs text-slate-400">
            Hai bisogno di aiuto? Scrivi a <a href="mailto:promozioni@besafebroker.it" className="text-[#0F7B85] underline">promozioni@besafebroker.it</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8F9] p-6" data-testid="setup-password-page">
      <div className="w-full max-w-md fade-up">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Attiva il tuo account</div>
          </div>
          <h1 className="font-display text-3xl mt-3 text-stone-900 leading-tight">
            Benvenuto/a, <span className="text-[#0F7B85]">{info?.name?.split(" ")[0] || "Cliente"}</span>!
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            Account creato per <strong className="text-slate-900">{info?.email}</strong>.
            Scegli una password per accedere alla tua dashboard e iniziare a presentare amici.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="setup-password-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Scegli una password</label>
              <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="setup-pwd-input"
                placeholder="Almeno 8 caratteri"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Conferma password</label>
              <input
                type="password"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="setup-pwd-confirm"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none"
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="setup-consent-checkbox"
                className="mt-0.5 rounded border-slate-300 text-[#0F7B85] focus:ring-[#0F7B85]"
              />
              <span>
                Accetto il <a href="/#regolamento" target="_blank" rel="noopener noreferrer" className="text-[#0F7B85] underline">regolamento</a>{" "}
                e la <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0F7B85] underline">privacy policy</Link> di Besafe Broker.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="setup-pwd-submit"
              className="w-full bg-[#0F7B85] hover:bg-[#0c6973] disabled:opacity-50 text-white rounded-lg px-4 py-3 font-semibold inline-flex items-center justify-center gap-2 transition"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Attiva il mio account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-xs text-slate-400 text-center">
            Hai già un account? <Link to="/login" className="text-[#0F7B85] underline">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
